import React from 'react';
import styled from 'styled-components';
import { Typography, Box, Container, Paper, Grid, Button } from '@mui/material';
import MovieFilterIcon from '@mui/icons-material/MovieFilter';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import DatabaseIcon from '@mui/icons-material/Storage'; // Using Storage as a proxy for Database
import { Link as RouterLink } from 'react-router-dom';

const HeroSection = styled(Box)`
  background: linear-gradient(135deg, #2c3e50 0%, #4ca1af 100%);
  color: white;
  padding: 80px 0;
  text-align: center;
  border-radius: 0 0 50% 50% / 20px;
  margin-bottom: 60px;
`;

const FeatureCard = styled(Paper)`
  padding: 30px;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  transition: transform 0.3s ease-in-out;
  border-radius: 16px; 

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 20px rgba(0,0,0,0.1);
  }
`;

const IconWrapper = styled.div`
  background-color: #e3f2fd;
  color: #1976d2;
  width: 60px;
  height: 60px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
  
  svg {
    font-size: 32px;
  }
`;

const About = () => {
    return (
        <Box sx={{ pb: 8 }}>
            <HeroSection>
                <Container maxWidth="md">
                    <Typography variant="h2" component="h1" gutterBottom sx={{ fontWeight: 'bold' }}>
                        Find Your Kritic Soulmate
                    </Typography>
                    <Typography variant="h5" sx={{ opacity: 0.9, mb: 4 }}>
                        Stop wasting time on movies you won't like. Discover critics who share your unique taste.
                    </Typography>
                    <Button
                        variant="contained"
                        size="large"
                        color="secondary"
                        component={RouterLink}
                        to="/"
                        sx={{ borderRadius: '25px', px: 4, py: 1.5, fontSize: '1.1rem', fontWeight: 'bold' }}
                    >
                        Start Rating Movies
                    </Button>
                </Container>
            </HeroSection>

            <Container maxWidth="lg">
                <Grid container spacing={4}>
                    <Grid item xs={12} md={6}>
                        <Box sx={{ pr: { md: 4 } }}>
                            <Typography variant="overline" color="primary" sx={{ fontWeight: 'bold', letterSpacing: 1.5 }}>
                                THE MISSION
                            </Typography>
                            <Typography variant="h3" gutterBottom sx={{ fontWeight: 'bold', mt: 1 }}>
                                Validating Your Taste
                            </Typography>
                            <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', color: 'text.secondary', lineHeight: 1.7 }}>
                                Review aggregators give you an average, but you aren't an average viewer. You have specific tastes, genres you love, and tropes you hate.
                            </Typography>
                            <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', color: 'text.secondary', lineHeight: 1.7 }}>
                                <strong>Kritics</strong> analyzes your movie ratings to find professional critics who think exactly like you do. When you match with a kritic, their reviews become a personalized recommendation engine just for you.
                            </Typography>
                        </Box>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Box sx={{ pl: { md: 4 } }}>
                            <Typography variant="overline" color="secondary" sx={{ fontWeight: 'bold', letterSpacing: 1.5 }}>
                                THE VISION
                            </Typography>
                            <Typography variant="h3" gutterBottom sx={{ fontWeight: 'bold', mt: 1 }}>
                                The Ultimate Archive
                            </Typography>
                            <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', color: 'text.secondary', lineHeight: 1.7 }}>
                                While other sites focus on movies, we focus on the observers. Our goal is to become <strong>the definitive database for critics</strong>.
                            </Typography>
                            <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', color: 'text.secondary', lineHeight: 1.7 }}>
                                We are building a comprehensive home for kritic profiles, their complete review histories, and their specific rating tendencies. No more scattering searches across the web—if a kritic said it, you'll find it here.
                            </Typography>
                        </Box>
                    </Grid>
                </Grid>

                <Box sx={{ mt: 10, mb: 6 }}>
                    <Typography variant="h4" align="center" gutterBottom sx={{ fontWeight: 'bold', mb: 6 }}>
                        How It Works
                    </Typography>
                    <Grid container spacing={4}>
                        <Grid item xs={12} sm={6} md={3}>
                            <FeatureCard elevation={2}>
                                <IconWrapper>
                                    <MovieFilterIcon />
                                </IconWrapper>
                                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                                    1. Rate Movies
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Rate movies you've seen on a 5-star scale. The more you rate, the accurately we can build your taste profile.
                                </Typography>
                            </FeatureCard>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <FeatureCard elevation={2}>
                                <IconWrapper>
                                    <CompareArrowsIcon />
                                </IconWrapper>
                                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                                    2. Get Matched
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Our algorithm compares your scores against thousands of critic reviews to find your highest-compatibility matches.
                                </Typography>
                            </FeatureCard>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <FeatureCard elevation={2}>
                                <IconWrapper>
                                    <PersonSearchIcon />
                                </IconWrapper>
                                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                                    3. Follow Kritics
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    See what your matched kritics are saying about new releases. If they like it, you probably will too.
                                </Typography>
                            </FeatureCard>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <FeatureCard elevation={2}>
                                <IconWrapper>
                                    <DatabaseIcon />
                                </IconWrapper>
                                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                                    4. Explore the DB
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Dive deep into kritic histories. See their average scores, favorite genres, and toughest reviews all in one place.
                                </Typography>
                            </FeatureCard>
                        </Grid>
                    </Grid>
                </Box>
            </Container>
        </Box>
    );
};

export default About;
